import React, { useCallback, useMemo, useState } from "react";

import { InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  IconButton,
  InputLabel,
  InputLabelProps,
  MenuItem,
  MenuItemProps,
  Select,
  SelectProps,
  Stack,
  SxProps,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useRenderDateValue } from "hooks";
import { useReportsQueriesSelector } from "store";
import { DateRange, DateRangeValue, DateRangeValueType } from "store/types";

import { dateRangeOptions } from "../../../constants";
import { DateRangeDialog } from "./DateRangeDialog";
import { QuarterRangeDialog } from "./QuarterRangeDialog";
import { SelectYearDialog } from "./SelectYearDialog";

// Overwritten styles
const formControlSx: SxProps = {
  minWidth: "180px",
  color: "#5d7782",
  fontSize: "15px"
};
const labelSx: SxProps = {
  color: "#5d7782"
};
const selectSx: SxProps = {
  borderRadius: "20px",
  backgroundColor: "#ffffff",
  ".MuiSelect-select": {
    marginBottom: 0
  },
  color: "#5d7782",
  fontSize: "inherit",

  ".MuiSvgIcon-root": {
    color: "inherit"
  },
  ".MuiSelect-outlined": {
    marginLeft: "6px",
    padding: "2.5px !important" //TODO: loose important
  },
  "&.MuiInputBase-root": {
    paddingRight: "25px",
    height: "27px"
  }
};

const menuItemSx: SxProps = {};
const helperTextSx: SxProps = {};

interface Props {
  disabled?: boolean; //flag of permission restriction
  value: DateRangeValue | null;
  onChange: (value: DateRangeValue, use_accounting_date?: boolean) => void;
  label?: string;
  /** Shown (muted) in the closed select when no range is selected. */
  placeholder?: string;
  error?: string;
  icon?: boolean;
  variant?: FormControlProps["variant"];
  formControlProps?: Omit<FormControlProps, "error" | "variant">;
  labelProps?: InputLabelProps;
  selectProps?: Omit<SelectProps, "value" | "label" | "onChange" | "renderValue" | "defaultValue" | "variant">;
  menuItemProps?: Omit<MenuItemProps, "key" | "value" | "children">;
  helperTextProps?: Omit<FormHelperTextProps, "children">;
  dateRangeDialog?: boolean;
  quarterRangeDialog?: boolean;
  enableInceptionToLastNAV?: boolean;
  hideOutline?: boolean;
  /** Hides the custom Date range dialog's "From Inception" checkbox and Effective/Accounting switch. */
  hideDateRangeExtras?: boolean;
}

export const SelectDateRange: React.FC<Props> = ({
  disabled,
  value,
  onChange,
  label = "",
  placeholder,
  error,
  variant = "outlined",
  formControlProps,
  labelProps,
  selectProps,
  menuItemProps,
  helperTextProps,
  dateRangeDialog = true,
  quarterRangeDialog = true,
  enableInceptionToLastNAV = false,
  hideOutline = false,
  hideDateRangeExtras = false
}) => {
  const { sx: incomingFormControlSx, ...restFormControlProps } = formControlProps || {};
  const { sx: incomingLabelSx, ...restLabelProps } = labelProps || {};
  const { sx: incomingSelectSx, ...restSelectProps } = selectProps || {};
  const { sx: incomingMenuItemSx, ...restMenuItemProps } = menuItemProps || {};
  const { sx: incomingHelperTextSx, ...restHelperTextProps } = helperTextProps || {};

  const previewDate = useRenderDateValue();
  const defaultUseAccountingDate = useReportsQueriesSelector()?.use_accounting_date || false;

  const [isDateRangeDialog, setIsDateRangeDialog] = useState<boolean>(false);
  const [isYearDialog, setIsYearDialog] = useState<boolean>(false);
  const [isQuarterRangeDialog, setIsQuarterRangeDialog] = useState<boolean>(false);

  const optionsToSelect = useMemo(() => {
    if (enableInceptionToLastNAV) return dateRangeOptions;

    return dateRangeOptions.filter((option) => option.value !== "inceptionToLastNAV");
  }, [enableInceptionToLastNAV]);

  const handleDataRangeSubmit = useCallback(
    (dateRange: DateRange, use_accounting_date?: boolean) => {
      onChange({ value: "dateRange", ...dateRange }, use_accounting_date);
    },
    [onChange]
  );

  const handleSpecificYearSubmit = useCallback(
    (dateRange: DateRange) => {
      onChange({ value: "selectYear", ...dateRange });
    },
    [onChange]
  );

  const handleQuarterToQuarterSubmit = useCallback(
    (dateRange: DateRange, use_accounting_date?: boolean) => {
      onChange({ value: "selectQuarter", ...dateRange }, use_accounting_date);
    },
    [onChange]
  );

  const onMenuItemClick = useCallback(
    (value: DateRangeValueType) => {
      // Handle dialog-based options
      if (
        (value === "dateRange" && dateRangeDialog) ||
        (value === "selectQuarter" && quarterRangeDialog) ||
        value === "selectYear"
      ) {
        // The click should be caught exactly on menu item
        return;
      }

      // Handle relative range calculations
      else if (value === "lastCalendarMonth") {
        const lastMonth = dayjs().subtract(1, "month");
        onChange({
          value: "lastCalendarMonth",
          from: lastMonth.startOf("month").toISOString(),
          to: lastMonth.endOf("month").toISOString()
        });
      } else if (value === "lastCalendarQuarter") {
        // Calculate last quarter manually since dayjs doesn't have quarter plugin
        const now = dayjs();
        const currentMonth = now.month(); // 0-11
        const currentQuarter = Math.floor(currentMonth / 3); // 0-3
        const lastQuarterMonth = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3; // Q4=9, Q1=0, Q2=3, Q3=6
        const lastQuarterYear = currentQuarter === 0 ? now.year() - 1 : now.year();

        const lastQuarterStart = dayjs().year(lastQuarterYear).month(lastQuarterMonth).startOf("month");
        const lastQuarterEnd = dayjs()
          .year(lastQuarterYear)
          .month(lastQuarterMonth + 2)
          .endOf("month");

        onChange({
          value: "lastCalendarQuarter",
          from: lastQuarterStart.toISOString(),
          to: lastQuarterEnd.toISOString()
        });
      } else if (value === "lastYear") {
        const lastYear = dayjs().subtract(1, "year");
        onChange({
          value: "lastYear",
          from: lastYear.startOf("year").toISOString(),
          to: lastYear.endOf("year").toISOString()
        });
      } else if (value === "ytd") {
        onChange({
          value: "ytd",
          from: dayjs().startOf("year").toISOString(),
          to: dayjs().toISOString()
        });
      } else if (value === "last3Years") {
        onChange({
          value: "last3Years",
          from: dayjs().subtract(3, "years").toISOString(),
          to: dayjs().toISOString()
        });
      } else if (value === "fromInception") {
        onChange({ value: "fromInception", to: dayjs().toISOString() });
      } else if (value === "inceptionToLastNAV") {
        onChange({ value: "inceptionToLastNAV" });
      }
      // Legacy support
      else {
        onChange({ value: value });
      }
    },
    [onChange, dateRangeDialog, quarterRangeDialog]
  );

  return (
    <>
      <FormControl
        disabled={disabled}
        variant={variant}
        sx={{ ...formControlSx, ...incomingFormControlSx }}
        error={!!error}
        {...restFormControlProps}
      >
        {label && (
          <InputLabel variant={variant} sx={{ ...labelSx, ...incomingLabelSx }} {...restLabelProps}>
            {label}
          </InputLabel>
        )}
        <Select<DateRangeValueType | null>
          value={value?.value}
          label={label}
          variant={variant}
          displayEmpty={Boolean(placeholder)}
          onChange={(event): void => onMenuItemClick(event.target.value as DateRangeValueType)}
          renderValue={() =>
            value ? (
              previewDate(value)
            ) : placeholder ? (
              <Box component="span" sx={{ color: "#92a7bc" }}>
                {placeholder}
              </Box>
            ) : (
              ""
            )
          }
          sx={{
            ...selectSx,
            ...incomingSelectSx,
            ...(hideOutline && {
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none"
              }
            })
          }}
          {...restSelectProps}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: "8px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)"
              }
            }
          }}
        >
          {/* Relative Range Section */}
          <Box display="flex" alignItems="center" padding="6px 20px">
            <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
              RELATIVE RANGE
            </Typography>
          </Box>
          {optionsToSelect
            .filter((option) => option.category === "relative")
            .map((option) => (
              <MenuItem
                sx={{
                  ...menuItemSx,
                  ...incomingMenuItemSx,
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
                {...restMenuItemProps}
                key={option.value}
                value={option.value}
                onClick={() => {
                  if (option.value === "selectQuarter") {
                    setIsQuarterRangeDialog(true);
                  }
                  if (option.value === "dateRange") {
                    setIsDateRangeDialog(true);
                  }
                  if (option.value === "selectYear") {
                    setIsYearDialog(true);
                  }
                }}
              >
                <span>{option.text}</span>
                {option.info && (
                  <MaterialTooltip title={option.info} isShow>
                    <IconButton sx={{ padding: 0, marginLeft: 1, width: 20, height: 20 }}>
                      <InfoOutlined sx={{ fontSize: 16, color: "#496682" }} />
                    </IconButton>
                  </MaterialTooltip>
                )}
              </MenuItem>
            ))}

          <Stack padding="0px 15px" margin="8px 0px">
            <Divider sx={{ width: "100%", bgcolor: "#DBE8F5", borderColor: "#DBE8F5" }} />
          </Stack>
          {/* Absolute Range Section */}
          <Box display="flex" alignItems="center" padding="0px 20px">
            <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
              ABSOLUTE RANGE
            </Typography>
          </Box>
          {optionsToSelect
            .filter((option) => option.category === "absolute")
            .map((option) => (
              <MenuItem
                sx={{
                  ...menuItemSx,
                  ...incomingMenuItemSx,
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
                {...restMenuItemProps}
                key={option.value}
                value={option.value}
                onClick={() => {
                  if (option.value === "selectQuarter") {
                    setIsQuarterRangeDialog(true);
                  }
                  if (option.value === "dateRange") {
                    setIsDateRangeDialog(true);
                  }
                  if (option.value === "selectYear") {
                    setIsYearDialog(true);
                  }
                }}
              >
                <span>{option.text}</span>
                {option.info && (
                  <MaterialTooltip title={option.info} isShow>
                    <IconButton sx={{ padding: 0, marginLeft: 1, width: 20, height: 20 }}>
                      <InfoOutlined sx={{ fontSize: 16, color: "#496682" }} />
                    </IconButton>
                  </MaterialTooltip>
                )}
              </MenuItem>
            ))}
        </Select>
        {error && (
          <FormHelperText sx={{ ...helperTextSx, ...incomingHelperTextSx }} {...restHelperTextProps}>
            Error
          </FormHelperText>
        )}
      </FormControl>
      <DateRangeDialog
        isOpen={dateRangeDialog && isDateRangeDialog}
        onClose={() => setIsDateRangeDialog(false)}
        onSubmit={handleDataRangeSubmit}
        defaultUseAccountingDate={defaultUseAccountingDate}
        hideExtraOptions={hideDateRangeExtras}
      />
      <QuarterRangeDialog
        isOpen={quarterRangeDialog && isQuarterRangeDialog}
        onClose={() => setIsQuarterRangeDialog(false)}
        onSubmit={handleQuarterToQuarterSubmit}
        defaultUseAccountingDate={defaultUseAccountingDate}
        hideExtraOptions={hideDateRangeExtras}
      />
      <SelectYearDialog
        isOpen={isYearDialog}
        onClose={() => setIsYearDialog(false)}
        onSubmit={handleSpecificYearSubmit}
      />
    </>
  );
};
